import apiClient, { getApiBaseUrl } from "../services/api/client";

const imageDataCache = new Map();

const API_BASE_URL = getApiBaseUrl();

function isLocalImageUrl(url) {
  return (
    typeof url === "string" &&
    (url.startsWith("blob:") || url.startsWith("data:"))
  );
}

function cacheKeyFor(imageUrl, options = {}) {
  if (options.creationId && isLocalImageUrl(imageUrl)) {
    return `creation:${options.creationId}`;
  }
  return imageUrl;
}

function imageDataFromImageElement(img, cacheKey) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const payload = {
    width: imageData.width,
    height: imageData.height,
    data: imageData.data,
  };
  imageDataCache.set(cacheKey, payload);
  return payload;
}

function loadImageElement(src, useCrossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCrossOrigin) {
      img.crossOrigin = "anonymous";
    }

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for pixel data"));
    img.src = src;
  });
}

function imageDataFromBlob(blob, cacheKey) {
  const objectUrl = URL.createObjectURL(blob);
  return loadImageElement(objectUrl, false)
    .then((img) => {
      URL.revokeObjectURL(objectUrl);
      return imageDataFromImageElement(img, cacheKey);
    })
    .catch((err) => {
      URL.revokeObjectURL(objectUrl);
      throw err;
    });
}

function isShareApiImageUrl(url) {
  try {
    const parsed = new URL(url);
    return /^\/api\/share\/[^/]+\/image$/.test(parsed.pathname);
  } catch {
    return false;
  }
}

async function fetchBlobForPixelAccess(imageUrl, options = {}) {
  const { creationId, getToken } = options;

  const isRemoteHttp =
    /^https?:\/\//i.test(imageUrl) && !isLocalImageUrl(imageUrl);

  if (isRemoteHttp) {
    const res = await fetch(imageUrl, { mode: "cors" });
    if (!res.ok) {
      throw new Error(`Remote image fetch failed (${res.status})`);
    }
    return res.blob();
  }

  if (creationId && typeof getToken === "function") {
    const token = await getToken();
    const res = await fetch(
      `${API_BASE_URL}/api/background-removal/creations/${creationId}/image`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!res.ok) {
      throw new Error(`Creation image fetch failed (${res.status})`);
    }
    return res.blob();
  }

  if (isShareApiImageUrl(imageUrl)) {
    const res = await fetch(imageUrl, { mode: "cors" });
    if (!res.ok) {
      throw new Error(`Share image fetch failed (${res.status})`);
    }
    return res.blob();
  }

  return null;
}

/**
 * Load image pixels once (shared by bbox scan and canvas alpha hit-test).
 * @param {string} imageUrl
 * @param {{ creationId?: number, getToken?: () => Promise<string|null> }} [options]
 * @returns {Promise<{ width: number, height: number, data: Uint8ClampedArray } | null>}
 */
export async function loadImageData(imageUrl, options = {}) {
  if (!imageUrl) {
    return null;
  }

  const cacheKey = cacheKeyFor(imageUrl, options);
  if (imageDataCache.has(cacheKey)) {
    return imageDataCache.get(cacheKey);
  }

  const isBlobOrData =
    imageUrl.startsWith("blob:") || imageUrl.startsWith("data:");
  const isRemoteHttp = /^https?:\/\//i.test(imageUrl);

  if (isRemoteHttp && !isBlobOrData) {
    try {
      const blob = await fetchBlobForPixelAccess(imageUrl, options);
      if (blob) {
        return imageDataFromBlob(blob, cacheKey);
      }
    } catch {
      // Fall through to crossOrigin Image load below.
    }
  }

  if (isBlobOrData) {
    const img = await loadImageElement(imageUrl, false);
    return imageDataFromImageElement(img, cacheKey);
  }

  try {
    const img = await loadImageElement(imageUrl, true);
    return imageDataFromImageElement(img, cacheKey);
  } catch {
    throw new Error("Failed to load image for pixel data");
  }
}

/**
 * Scan alpha channel for opaque pixel bounds (inclusive width/height).
 * @param {{ data: Uint8ClampedArray, width: number, height: number }} imageData
 * @param {number} [threshold=16]
 * @returns {{ left: number, top: number, right: number, bottom: number, width: number, height: number } | null}
 */
export function getOpaqueBounds(imageData, threshold = 16) {
  if (!imageData?.data) {
    return null;
  }

  const { data, width, height } = imageData;
  let top = null;
  let left = null;
  let right = null;
  let bottom = null;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > threshold) {
        if (top === null) top = y;
        if (left === null || x < left) left = x;
        if (right === null || x > right) right = x;
        bottom = y;
      }
    }
  }

  if (top === null || left === null || right === null || bottom === null) {
    return null;
  }

  return {
    left,
    top,
    right,
    bottom,
    width: right - left + 1,
    height: bottom - top + 1,
  };
}

/** Fraction of pixels with alpha <= threshold (0..1). */
export function estimateTransparentRatio(imageData, threshold = 16) {
  if (!imageData?.data || !imageData.width || !imageData.height) {
    return 0;
  }
  const { data, width, height } = imageData;
  const total = width * height;
  let transparent = 0;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] <= threshold) {
      transparent += 1;
    }
  }
  return transparent / total;
}

/** True when the cutout has real transparency (not a flat opaque matte / watermark fill). */
export function maskHasTransparentPixels(imageData, threshold = 16, minRatio = 0.005) {
  return estimateTransparentRatio(imageData, threshold) >= minRatio;
}

/** Ignore faint sandbox watermarks when picking subject vs canvas (PhotoRoom sandbox). */
export const SUBJECT_HIT_ALPHA_THRESHOLD = 128;

/**
 * Tight subject AABB — tries high alpha thresholds first so faint watermarks
 * on transparent margins (e.g. PhotoRoom sandbox) do not expand the box to full canvas.
 */
export function getSubjectBounds(imageData, maxCoverage = 0.9) {
  if (!imageData?.data || !imageData.width || !imageData.height) {
    return null;
  }

  const total = imageData.width * imageData.height;
  const thresholds = [240, 200, 160, 128, 96, 48, 16];
  let tightestAboveMax = null;
  let tightestCoverage = 1;

  for (const threshold of thresholds) {
    const bounds = getOpaqueBounds(imageData, threshold);
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
      continue;
    }
    const coverage = (bounds.width * bounds.height) / total;
    if (coverage < maxCoverage) {
      return bounds;
    }
    if (coverage < tightestCoverage) {
      tightestCoverage = coverage;
      tightestAboveMax = bounds;
    }
  }

  // Headshot + watermark: all thresholds may exceed maxCoverage — use the tightest
  // box rather than falling back to the full-frame alpha>16 bounds.
  if (tightestAboveMax && tightestCoverage < 0.98) {
    return tightestAboveMax;
  }

  return getOpaqueBounds(imageData, 16);
}

export async function getBoundingBox(imageUrl, options = {}) {
  const loaded = await loadImageData(imageUrl, options);
  if (!loaded) {
    return {
      top: null,
      left: null,
      right: null,
      bottom: null,
      width: 0,
      height: 0,
    };
  }

  const bounds = getOpaqueBounds(loaded, 0);
  if (!bounds) {
    return {
      top: null,
      left: null,
      right: null,
      bottom: null,
      width: 0,
      height: 0,
    };
  }

  return {
    top: bounds.top,
    left: bounds.left,
    right: bounds.right,
    bottom: bounds.bottom,
    width: bounds.width,
    height: bounds.height,
  };
}
